import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useCreateTermMutation,
  useDeleteTermMutation,
  useLazyFetchTermsByCodeQuery,
  useLazyFetchTermsQuery,
  useSaveTermsOrderMutation,
  useUpdateTermMutation,
} from '../api/app-terms.api';
import {
  AppTermsAC,
  clearState,
  setAppTermsModal,
  setIsNeedSaveOrder,
  setSelectedTerm,
} from '../store/app-terms.slice';
import {
  CreateAppTermPayload,
  DeleteAppTermPayload,
  FetchTermsByCodePayload,
  IAppTerm,
  IAppTermsModal,
  SaveTermsOrderPayload,
  UpdateAppTermPayload,
} from '../types/app-terms.types';

interface OpenModal {
  modal: IAppTermsModal;
  term: IAppTerm | null;
}

interface UseAppTerms {
  clearTermsState: () => void;
  openTermsModal: (payload: OpenModal) => void;
  closeTermsModal: () => void;
  fetchTermsHandler: () => Promise<boolean>;
  createTermHandler: (payload: CreateAppTermPayload) => Promise<IAppTerm | null>;
  updateTermHandler: (payload: UpdateAppTermPayload) => Promise<IAppTerm | null>;
  deleteTermHandler: (payload: DeleteAppTermPayload) => Promise<boolean>;
  saveTermsOrderHandler: (payload: SaveTermsOrderPayload) => Promise<boolean>;
  fetchTermsByCodeHandler: (payload: FetchTermsByCodePayload) => Promise<boolean>;
}

export const useAppTerms = (): UseAppTerms => {
  const dispatch = useAppDispatch();

  const [fetchTermsQuery] = useLazyFetchTermsQuery();
  const [createTermQuery] = useCreateTermMutation();
  const [updateTermQuery] = useUpdateTermMutation();
  const [deleteTermQuery] = useDeleteTermMutation();
  const [saveTermsOrderQuery] = useSaveTermsOrderMutation();
  const [fetchTermsByCodeQuery] = useLazyFetchTermsByCodeQuery();

  const fetchTermsHandler = async () => {
    dispatch(AppTermsAC.toggleLoading(true));
    const { data: response, error } = await fetchTermsQuery();
    dispatch(AppTermsAC.toggleLoading(false));
    const isError = error || !response?.data;

    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Terms!');
    } else {
      batch(() => {
        dispatch(AppTermsAC.setItems(response.data));
      });
    }

    return !isError;
  };

  const fetchTermsByCodeHandler = async (payload: FetchTermsByCodePayload) => {
    const { data: response, error } = await fetchTermsByCodeQuery(payload);

    const isError = error || !response?.data;

    if (!isError) {
      batch(() => {
        dispatch(AppTermsAC.setItems(response.data));
      });
    }

    return !isError;
  };

  const createTermHandler = async (payload: CreateAppTermPayload) => {
    dispatch(AppTermsAC.toggleLoading(true));
    const { data: response, error } = await createTermQuery(payload);
    dispatch(AppTermsAC.toggleLoading(false));
    const isError = error || !response?.data?.id;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to create Term!');
      return null;
    } else {
      toast.success('Term successfully created!');
    }
    dispatch(AppTermsAC.addItem(response.data));
    await fetchTermsHandler();
    return response.data;
  };

  const updateTermHandler = async (payload: UpdateAppTermPayload) => {
    dispatch(AppTermsAC.toggleLoading(true));
    const { data: response, error } = await updateTermQuery(payload);
    dispatch(AppTermsAC.toggleLoading(false));
    const isError = error || !response?.data?.id;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to update Term!');
      return null;
    } else {
      toast.success('Term successfully updated!');
    }
    dispatch(AppTermsAC.editItem(response.data));
    return response.data;
  };

  const deleteTermHandler = async (payload: DeleteAppTermPayload) => {
    dispatch(AppTermsAC.toggleLoading(true));
    const { error } = await deleteTermQuery(payload);
    dispatch(AppTermsAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to remove Term!');
    } else {
      toast.success('Term successfully removed!');
      dispatch(AppTermsAC.removeItem(payload.id));
    }

    return !error;
  };

  const saveTermsOrderHandler = async (payload: SaveTermsOrderPayload) => {
    dispatch(AppTermsAC.toggleLoading(true));
    const { error } = await saveTermsOrderQuery(payload);
    dispatch(AppTermsAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to save Order!');
    } else {
      toast.success('Order successfully saved!');
      dispatch(setIsNeedSaveOrder(false));
    }
    return !error;
  };

  const openTermsModal = ({ modal, term }: OpenModal) => {
    batch(() => {
      dispatch(setSelectedTerm(term));
      dispatch(setAppTermsModal(modal));
    });
  };

  const closeTermsModal = () => {
    batch(() => {
      dispatch(setSelectedTerm(null));
      dispatch(setAppTermsModal(null));
    });
  };

  const clearTermsState = () => {
    dispatch(clearState());
  };

  return {
    clearTermsState,
    openTermsModal,
    closeTermsModal,
    fetchTermsHandler,
    createTermHandler,
    updateTermHandler,
    deleteTermHandler,
    saveTermsOrderHandler,
    fetchTermsByCodeHandler,
  };
};
